import React, { useState, useMemo } from 'react';
import {
    Paper,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    TextField,
    InputAdornment,
    Button,
    Chip,
} from '@mui/material';
import {
    Search,
    FileDownload,
    Room,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MEASURE_TYPES, MEASURE_CONFIG, getMeasureIcon, getMeasureLabel } from '../../utils/measurementConfig';
import { getMonthName, exportMonthlyDataToCsv } from '../../utils/monthlyDataUtils';

function MonthlyBreakdownTable({ normalizedData = [], selectedYear }) {
    const { t } = useTranslation();

    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState('month');
    const [order, setOrder] = useState('asc');

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleExport = () => {
        exportMonthlyDataToCsv(filteredData, selectedYear, t);
    };

    // Filter by search term
    const filteredData = useMemo(() => {
        if (!searchTerm.trim()) return normalizedData;
        const lower = searchTerm.toLowerCase();
        return normalizedData.filter((item) => {
            const zoneMatch = item.zone?.toLowerCase().includes(lower);
            const monthMatch = getMonthName(item.month, t).toLowerCase().includes(lower);
            return zoneMatch || monthMatch;
        });
    }, [normalizedData, searchTerm, t]);

    // Sort data
    const sortedData = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            let aVal;
            let bVal;

            if (orderBy === 'month') {
                aVal = a.month;
                bVal = b.month;
            } else if (orderBy === 'zone') {
                aVal = a.zone || '';
                bVal = b.zone || '';
            } else if (MEASURE_TYPES.includes(orderBy)) {
                aVal = a.measures[orderBy] ?? -Infinity;
                bVal = b.measures[orderBy] ?? -Infinity;
            } else {
                aVal = a.month;
                bVal = b.month;
            }

            if (typeof aVal === 'string') {
                return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return order === 'asc' ? aVal - bVal : bVal - aVal;
        });
    }, [filteredData, orderBy, order]);

    const paginatedData = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedData.slice(start, start + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    return (
        <Paper
            elevation={2}
            sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
            }}
        >
            {/* Header & Controls */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2.5,
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t('common.breakdownTable', 'Monthly Breakdown Data')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {t('common.showingResults', { count: filteredData.length })}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder={t('common.filterByZone', 'Filter by zone...')}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ minWidth: 200 }}
                    />

                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownload />}
                        onClick={handleExport}
                        disabled={filteredData.length === 0}
                        sx={{ textTransform: 'none', height: 40 }}
                    >
                        {t('common.exportCsv', 'Export CSV')}
                    </Button>
                </Box>
            </Box>

            {/* Table */}
            <TableContainer sx={{ maxHeight: 520, borderRadius: 1 }}>
                <Table stickyHeader size="small" aria-label="monthly averages table">
                    <TableHead>
                        <TableRow>
                            {/* Month */}
                            <TableCell sx={{ fontWeight: 700 }}>
                                <TableSortLabel
                                    active={orderBy === 'month'}
                                    direction={orderBy === 'month' ? order : 'asc'}
                                    onClick={() => handleSort('month')}
                                >
                                    {t('common.month', 'Month')}
                                </TableSortLabel>
                            </TableCell>

                            {/* Zone */}
                            <TableCell sx={{ fontWeight: 700 }}>
                                <TableSortLabel
                                    active={orderBy === 'zone'}
                                    direction={orderBy === 'zone' ? order : 'asc'}
                                    onClick={() => handleSort('zone')}
                                >
                                    {t('common.zone', 'Zone')}
                                </TableSortLabel>
                            </TableCell>

                            {/* Measurements */}
                            {MEASURE_TYPES.map((type) => {
                                const config = MEASURE_CONFIG[type];
                                return (
                                    <TableCell key={type} align="right" sx={{ fontWeight: 700 }}>
                                        <TableSortLabel
                                            active={orderBy === type}
                                            direction={orderBy === type ? order : 'asc'}
                                            onClick={() => handleSort(type)}
                                        >
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                {getMeasureIcon(type, { fontSize: 16 })}
                                                <span>{getMeasureLabel(type, t)} ({config.unit})</span>
                                            </Box>
                                        </TableSortLabel>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row, index) => {
                                return (
                                    <TableRow
                                        hover
                                        key={`${row.year}-${row.month}-${row.zone}-${index}`}
                                        sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                                    >
                                        {/* Month */}
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {getMonthName(row.month, t)} {row.year}
                                        </TableCell>

                                        {/* Zone */}
                                        <TableCell>
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                                <Room sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {row.zone}
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        {/* TEMP */}
                                        <TableCell align="right">
                                            {row.measures.TEMP !== undefined && row.measures.TEMP !== null ? (
                                                <Chip
                                                    label={`${row.measures.TEMP.toFixed(1)} °C`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: MEASURE_CONFIG.TEMP.lightBg,
                                                        color: MEASURE_CONFIG.TEMP.color,
                                                        fontWeight: 600,
                                                        fontSize: '0.8rem',
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.disabled">—</Typography>
                                            )}
                                        </TableCell>

                                        {/* HUMI */}
                                        <TableCell align="right">
                                            {row.measures.HUMI !== undefined && row.measures.HUMI !== null ? (
                                                <Chip
                                                    label={`${row.measures.HUMI.toFixed(1)} %`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: MEASURE_CONFIG.HUMI.lightBg,
                                                        color: MEASURE_CONFIG.HUMI.color,
                                                        fontWeight: 600,
                                                        fontSize: '0.8rem',
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.disabled">—</Typography>
                                            )}
                                        </TableCell>

                                        {/* POWE */}
                                        <TableCell align="right">
                                            {row.measures.POWE !== undefined && row.measures.POWE !== null ? (
                                                <Chip
                                                    label={`${row.measures.POWE.toFixed(2)} kW`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: MEASURE_CONFIG.POWE.lightBg,
                                                        color: MEASURE_CONFIG.POWE.color,
                                                        fontWeight: 600,
                                                        fontSize: '0.8rem',
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.disabled">—</Typography>
                                            )}
                                        </TableCell>

                                        {/* PRES */}
                                        <TableCell align="right">
                                            {row.measures.PRES !== undefined && row.measures.PRES !== null ? (
                                                <Chip
                                                    label={`${row.measures.PRES.toFixed(1)} hPa`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: MEASURE_CONFIG.PRES.lightBg,
                                                        color: MEASURE_CONFIG.PRES.color,
                                                        fontWeight: 600,
                                                        fontSize: '0.8rem',
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.disabled">—</Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('common.noData', 'No data available')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
}

export default MonthlyBreakdownTable;
